import { useEffect, useRef, useState } from 'react';
import type { AgentState } from '../types/agent-state.types';
import type { RunSignal } from './use-assistant-chat';

/**
 * Anti-flicker hold: after the last tool phase, `searching` persists this long
 * even if a non-tool phase lands, so multi-tool runs don't strobe.
 */
export const SEARCHING_HOLD_MS = 300;

/** How long `done` shows (once per runId) before settling back to `idle`. */
export const DONE_SETTLE_MS = 700;

export interface UseAgentStateInput {
  /** The latest run-lifecycle transition from useAssistantChat. */
  runSignal: RunSignal;
  /** A Level-4 confirmation is pending — overrides every run phase. */
  hasConfirmation: boolean;
  /** The chat hook surfaced an error (stream/transport), no RUN_ERROR needed. */
  hasError: boolean;
}

/** A settled/pending marker that can hold a null runId distinctly from "none". */
type RunMarker = { runId: string | null } | null;

interface Internals {
  /** Timestamp of the most recent 'tool' phase (0 = never). */
  lastToolAt: number;
  /** The run that owns lastToolAt — the hold never crosses runs. */
  lastToolRun: string | null;
  /** The run whose 'finished' already popped done and settled to idle. */
  settled: RunMarker;
  /** The run whose done-timer is (or was) counting down. */
  doneFor: RunMarker;
  /**
   * The exact signal on screen when hasError rose. Only a strictly DIFFERENT
   * active signal supersedes the error — identity, not timestamps, so a
   * same-millisecond error is never masked by the signal it interrupted.
   */
  errorSignal: RunSignal | null;
  hadError: boolean;
}

/**
 * Derives TIA's 7-state machine from the run signal + hook flags.
 *
 * Pure over its inputs except for two internal timers:
 * - a done-settle timer (`done` → `idle` after ~0.7s, once per runId), and
 * - a hold re-evaluation timer (re-checks `searching` when the 300ms
 *   anti-flicker window closes).
 *
 * Precedence: confirm > error > finished(done/idle) > searching(+hold) >
 * writing > thinking > idle.
 */
export function useAgentState(input: UseAgentStateInput): AgentState {
  const { runSignal, hasConfirmation, hasError } = input;

  const [state, setState] = useState<AgentState>('idle');
  const internals = useRef<Internals>({
    lastToolAt: 0,
    lastToolRun: null,
    settled: null,
    doneFor: null,
    errorSignal: null,
    hadError: false,
  });
  const doneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const evaluateRef = useRef<() => void>(() => {});

  useEffect(() => {
    const evaluate = (): void => {
      const r = internals.current;
      const now = Date.now();
      const { runId, phase, at } = runSignal;

      // Capture the exact signal the error rose over; clear it when the hook
      // error clears so a later error is re-captured against its own signal.
      if (hasError && !r.hadError) r.errorSignal = { runId, phase, at };
      if (!hasError) r.errorSignal = null;
      r.hadError = hasError;

      if (phase === 'tool' && at >= r.lastToolAt) {
        r.lastToolAt = at;
        r.lastToolRun = runId;
      }
      // A fresh run: the previous run's settle no longer applies, so a reused
      // runId (e.g. a confirmation resume) can still pop done when it finishes.
      if (phase === 'started') r.settled = null;

      // A different active signal than the one the error interrupted means a
      // new send superseded the failure — compared by identity, never by
      // timestamp, so a same-ms error still wins over the signal it landed on.
      const activeAfterError =
        (phase === 'started' || phase === 'tool' || phase === 'text') &&
        !(
          r.errorSignal !== null &&
          r.errorSignal.runId === runId &&
          r.errorSignal.phase === phase &&
          r.errorSignal.at === at
        );

      let next: AgentState;
      if (hasConfirmation) {
        // The real stream emits CUSTOM confirmation THEN RUN_FINISHED —
        // confirm overrides finished, and a reject (no new run) lands on
        // idle, never a late done pop.
        next = 'confirm';
        if (phase === 'finished') r.settled = { runId };
      } else if (phase === 'error' || (hasError && !activeAfterError)) {
        next = 'error';
      } else if (phase === 'finished') {
        if (r.settled && r.settled.runId === runId) {
          next = 'idle';
        } else {
          next = 'done';
          const timerAlreadyRunning =
            doneTimer.current !== null &&
            r.doneFor !== null &&
            r.doneFor.runId === runId;
          if (!timerAlreadyRunning) {
            if (doneTimer.current !== null) clearTimeout(doneTimer.current);
            r.doneFor = { runId };
            doneTimer.current = setTimeout(() => {
              doneTimer.current = null;
              internals.current.settled = { runId };
              evaluateRef.current();
            }, DONE_SETTLE_MS);
          }
        }
      } else if (phase === 'tool') {
        next = 'searching';
      } else if (
        (phase === 'started' || phase === 'text') &&
        runId === r.lastToolRun &&
        now - r.lastToolAt < SEARCHING_HOLD_MS
      ) {
        // Anti-flicker hold: still within the window after the last tool
        // phase. Re-evaluate when it closes so the state can revert.
        next = 'searching';
        if (holdTimer.current === null) {
          holdTimer.current = setTimeout(
            () => {
              holdTimer.current = null;
              evaluateRef.current();
            },
            r.lastToolAt + SEARCHING_HOLD_MS - now
          );
        }
      } else if (phase === 'text') {
        next = 'writing';
      } else if (phase === 'started') {
        next = 'thinking';
      } else {
        next = 'idle';
      }
      setState(next);
    };

    evaluateRef.current = evaluate;
    evaluate();
  }, [runSignal, hasConfirmation, hasError]);

  // Unmount: drop both timers (their callbacks would setState).
  useEffect(
    () => () => {
      if (doneTimer.current !== null) clearTimeout(doneTimer.current);
      if (holdTimer.current !== null) clearTimeout(holdTimer.current);
    },
    []
  );

  return state;
}
